import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import { role } from "../../Constants/role";

const UnifiedTable = memo(({
  data,
  handleAction,
  onManagePermission,
  onManageRole,
  isSuperAdmin,
  getDataLists,
  tableType, // 'user', 'cityAdmin', 'superCityAdmin'
  emptyMessageKey,
}) => {
  const { t } = useTranslation();
  
  // Pagination state
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [count, setCount] = useState(data?.count || data?.data?.length || 0);
  
  // Calculate total pages
  const totalPages = Math.ceil(count / pageSize) || 1;
  
  // Update pagination when data changes
  useEffect(() => {
    if (data) {
      setPageNo(data.pageNo || 1);
      setPageSize(data.pageSize || 10);
      setCount(data.count || data?.data?.length || 0);
    }
  }, [data]);
  
  // Handler for page change
  const handlePageChange = useCallback((newPageNo) => {
    if (newPageNo >= 1 && newPageNo <= totalPages && newPageNo !== pageNo) {
      setPageNo(newPageNo);
      if (getDataLists) {
        getDataLists(newPageNo, pageSize);
      }
    }
  }, [totalPages, pageNo, getDataLists, pageSize]);

  // Memoize column headers based on table type
  const columnHeaders = useMemo(() => {
    const baseHeaders = [
      { key: 'name', label: t("name") || "Name" },
      { key: 'username', label: t("username") || "Username" },
      { key: 'email', label: t("email") || "Email" },
    ];

    if (tableType === 'cityAdmin' || tableType === 'superCityAdmin') {
      baseHeaders.push(
        { key: 'city', label: t("city") || "City" },
        { key: 'role', label: t("role") || "Role" }
      );
    }

    baseHeaders.push(
      { key: 'permissions', label: t("permissions") || "Permissions" },
      { key: 'actions', label: t("actions") || "Actions" }
    );

    return baseHeaders;
  }, [tableType, t]);

  // Memoize empty message
  const emptyMessage = useMemo(() => {
    return emptyMessageKey ? t(emptyMessageKey) : t("no_users_found") || "No Users Found";
  }, [emptyMessageKey, t]);

  // Memoize row renderer
  const renderRow = useCallback((item) => {
    const getRoleDisplay = (roleId) => {
      if (tableType === "user") return "—";
     
      const roleKey = Object.keys(role).find(
        (key) => role[key] === roleId
      );
     
      if (!roleKey) return "N/A";
     
      if (tableType === "cityAdmin" && roleKey === "TerminalAdmin") {
        return t("city_admin", "City Admin");
      }
     
      if (
        tableType === "superCityAdmin" &&
        ["SuperAdmin", "superCityAdmin"].includes(roleKey)
      ) {
        return t("super_city_admins", "Super City Admin");
      }
     
      return roleKey;
    };

    const getCityDisplay = (cities) => {
      if (!cities || !Array.isArray(cities)) return "";
      return cities.map((city) => city.cityName).join(", ");
    };

    return (
      <tr key={item.id} className="border-t hover:bg-gray-50">
        <td className="py-3 px-4">{item.name}</td>
        <td className="py-3 px-4">{item.username}</td>
        <td className="py-3 px-4">{item.email}</td>
        {(tableType === 'cityAdmin' || tableType === 'superCityAdmin') && (
          <>
            <td className="py-3 px-4">{getCityDisplay(item.cities)}</td>
            <td className="py-3 px-4">{getRoleDisplay(item.roleId)}</td>
          </>
        )}
        <td className="py-3 px-4">
          {tableType === 'user' ? (
            <button
              onClick={() => onManagePermission(item)}
              className="px-3 py-1 text-xs bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition"
              title={t("manage_permissions") || "Manage Permissions"}
            >
              {t("manage") || "Manage"}
            </button>
          ) : (
            <button
              onClick={() => onManageRole(item)}
              className="px-3 py-1 text-xs bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition"
              title={t("change_role1") || "Change Role"}
            >
              {t("change_role1") || "Change Role"}
            </button>
          )}
        </td>
        <td className="py-3 px-4 mt-4 flex flex-row">
          {item.status === 1 ? (
            <button
              onClick={() => handleAction(item.id, tableType, pageNo, pageSize)}
              className="hover:opacity-80 transition mr-3"
              title={t("active") || "Active"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-green-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </button>
          ) : (
            <button
              onClick={() => handleAction(item.id, tableType, pageNo, pageSize)}
              className="hover:opacity-80 transition mr-3"
              title={t("inactive") || "InActive"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-red-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </button>
          )}
        </td>
      </tr>
    );
  }, [tableType, onManagePermission, onManageRole, handleAction, pageNo, pageSize, t]);

  // Calculate colspan for empty state
  const emptyColSpan = useMemo(() => {
    return tableType === 'user' ? 8 : 10;
  }, [tableType]);

  return (
    <div className="overflow-x-auto rounded-xl shadow">
      <table className="min-w-full bg-white">
        <thead className="bg-gray-100 text-left">
          <tr>
            {columnHeaders.map((header) => (
              <th key={header.key} className="py-3 px-4">
                {header.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.data?.length === 0 ? (
            <tr className="border-t hover:bg-gray-50 text-center">
              <td colSpan={emptyColSpan} className="py-4 px-6 text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data?.data?.map((item) => renderRow(item))
          )}
        </tbody>
      </table>
      
      {/* Pagination */}
      <div className="flex justify-center mt-4 p-4 space-x-2">
        <button
          onClick={() => handlePageChange(Math.max(pageNo - 1, 1))}
          disabled={pageNo === 1}
          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        <span className="px-4 py-1 text-sm text-slate-50">
          {t("page")} {pageNo} {t("of")} {totalPages}
        </span>

        <button
          onClick={() => handlePageChange(Math.min(pageNo + 1, totalPages))}
          disabled={pageNo === totalPages}
          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  );
});

UnifiedTable.displayName = 'UnifiedTable';

export default UnifiedTable;

